import React, { Component } from "react";
import i18n from "../../../../Utils/i18next";
import { Select, Input, Button } from "../../ui";
import PropTypes from "prop-types";
import {
  CloseCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import "./search.css"
import { getPopupContainer } from "../../../../Utils/Global";

const { Option } = Select;

/**
 * Search component.
 * Includes input area and search category.
 * Search functions.
 */
export default class Search extends Component {
  constructor(props) {
    super(props);

    this.prototype = {
      paddingNone: PropTypes.bool,
      marginBottom: PropTypes.string,
      inputStyle: PropTypes.object,
      id: PropTypes.string,
      width: PropTypes.string,
      allowClear: PropTypes.bool,
      searchBarVisible: PropTypes.bool,
      onChange: PropTypes.func,
      onClear: PropTypes.func,
      searchItemList: PropTypes.array,
      searchCategory: PropTypes.func,
      selectedSearchCategory: PropTypes.object,
      showSearchButton: PropTypes.bool,
      searchWithEnter: PropTypes.bool,
      getPopupContainer: PropTypes.func,
    };

    this.state = {
      placeholder: this.props.placeholder
        ? this.props.placeholder
        : i18n.t("Search"),
      categoryPlaceholder: this.props.categoryPlaceholder
        ? this.props.categoryPlaceholder
        : i18n.t("Category"),
      width: this.props.width,
      searchData: this.props.value !== undefined ? this.props.value : "",
      searched: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.value !== prevProps.value &&
      this.props.value !== this.state.searchData
    ) {
      this.setState({ searchData: this.props.value ?? "" });
    }
  }

  onChange = (e) => {
    let value = e.target.value;
    let searched = !!value;

    this.setState(
      {
        ...this.state,
        searched: searched,
        searchData: value || "",
      },
      () => {
        // If neither button nor enter-mandatory is set, search as user types
        if (!this.props.showSearchButton && !this.props.searchWithEnter) {
          this.handleSearch();
        }
      },
    );
  };

  handleSearch = () => {
    if (this.props.onChange instanceof Function) {
      this.props.onChange({
        target: {
          id: this.props.id,
          value: this.state.searchData,
        },
      });
    }
  };

  onKeyUp = (e) => {
    if (e.key === "Enter") {
      if (this.props.showSearchButton || this.props.searchWithEnter) {
        this.handleSearch();
      }

      return;
    }
  };

  onFocus = (e) => {
    if (this.props.onFocus instanceof Function) {
      this.props.onFocus(e);
    }
  };

  onBlur = (e) => {
    if (this.props.onBlur instanceof Function) {
      this.props.onBlur(e);
    }
  };

  handleCategoryChange = (value) => {
    if (!this.props.searchCategory) return;

    if (!value) {
      this.props.searchCategory({});
      return;
    }

    const selected = this.props.searchItemList.find(
      (item) => item.name === value,
    );
    if (selected) {
      this.props.searchCategory(selected);
    }
  };

  clear = () => {
    if (typeof this.props.onClear === "function") {
      this.props.onClear();
    } 
    
    this.setState({
      searchData: "",
      searchCategory: {},
    }, () => {
        // If neither button nor enter-mandatory is set, search as user types
        if (!this.props.showSearchButton && !this.props.searchWithEnter) {
          this.handleSearch();
        }
      });
  }

  render() {
    if (this.props.searchBarVisible === false) return null;

    const DEFAULT_ICON = "fas fa-th-large";
    const hasCategory =
      this.props.selectedSearchCategory &&
      this.props.selectedSearchCategory.name !== undefined;

    return (
      <div
        className={`search-component-v2 ${hasCategory ? "has-category" : ""} ${this.props.searchItemList ? "has-search-items" : "no-search-items"}`}
        style={{
          padding: this.props.paddingNone ? 0 : "0 25px",
          marginBottom: this.props.marginBottom ? this.props.marginBottom : 0,
          width: this.state.width || "100%",
        }}
      >
        <div
          className="search-input-group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          {this.props.searchItemList && (
            <Select
              className="search-category-select"
              value={this.props.selectedSearchCategory?.name || undefined}
              onChange={this.handleCategoryChange}
              placeholder={
                <div className="category-option-content">
                  <i className={DEFAULT_ICON} style={{ fontSize: "12px" }}></i>
                  <span className="category-option-text">
                    {this.state.categoryPlaceholder}
                  </span>
                </div>
              }
              allowClear
              dropdownMatchSelectWidth={false}
              optionLabelProp="label"
              dropdownClassName={this.props.dropdownClassName}
              getPopupContainer={this.props.getPopupContainer || getPopupContainer}
              data-testid={this.props.dataTestId ? this.props.dataTestId + "-category-select" : null}
            >
              {this.props.searchItemList.map((item, index) => (
                <Option
                  key={index}
                  value={item.name}
                  label={
                    <div className="category-option-content">
                      <i
                        className={item.icon || DEFAULT_ICON}
                        style={{ fontSize: "12px" }}
                      ></i>
                      <span className="category-option-text">
                        {i18n.t(item.translate)}
                      </span>
                    </div>
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {item.icon && (
                      <i
                        className={item.icon}
                        style={{ fontSize: "12px", opacity: 0.7 }}
                      ></i>
                    )}
                    <span>{i18n.t(item.translate)}</span>
                  </div>
                </Option>
              ))}
            </Select>
          )}
          <Input
            onDrop={
              this.props.preventDrop
                ? (event) => {
                    event.preventDefault();
                  }
                : null
            }
            name={this.props.name}
            style={{
              flex: 1,
              textAlign: "left",
              ...this.props.inputStyle,
            }}
            autoComplete="off"
            className="search-main-input"
            type="search"
            placeholder={
              this.props.disablePlaceHolder ? "" : this.state.placeholder
            }
            aria-label={this.state.placeholder}
            value={this.state.searchData}
            id={this.props.id}
            onChange={this.onChange}
            onKeyUp={this.onKeyUp}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            autoFocus={this.props.autoFocus}
            suffix={
              <>
                {this.props.allowClear !== false && this.state.searchData && (
                  <CloseCircleFilled onClick={this.clear} />
                )}

                { this.props.suffix }
              </>
            }
            data-testid={this.props.dataTestId ? this.props.dataTestId + "-input" : null}
          />
          {this.props.showSearchButton && (
            <Button
              type="primary"
              onClick={this.handleSearch}
              style={{ height: "40px", minWidth: "40px" }}
              shape="circle"
              icon={<SearchOutlined />}
              data-testid={this.props.dataTestId ? this.props.dataTestId + "-search-button" : null}
            />
          )}
        </div>

        {this.props.children}

        <style>{`

        `}</style>
      </div>
    );
  }
}
